import { endPoints } from '@/api/configs';
import { NotificationsRes } from '@/api/types/notificationsType';
import { useGet } from '@/api/useApi';
import { useNotificationReadStore } from '@/core/notificationReadStore';
import { useProfileStore } from '@/core/useUserProfile';
import { axiosInstance } from '@/rest-data-provider/utils/axios';
import {
  filterNotificationsByAudience,
  filterNotificationsByTargetAdmin,
} from '@/utils/filterNotificationsByAudience';
import { toLocallyReadIdSet } from '@/utils/localNotificationReads';
import { normalizeApiNotifications } from '@/utils/normalizeNotificationFeed';
import { applyLocalReadState } from '@/utils/notificationUnread';
import { parseUnreadCount } from '@/utils/parseUnreadCount';
import {
  parseNotificationsList,
  parseNotificationsTotalCount,
} from '@/utils/parseNotificationsResponse';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

const POLL_INTERVAL_MS = 15_000;
const FEED_PAGE_SIZE = 50;

const applyAudienceFilters = (
  notifications: NotificationsRes[],
  adminType?: string,
  adminId?: number
): NotificationsRes[] =>
  filterNotificationsByTargetAdmin(
    filterNotificationsByAudience(notifications, adminType),
    adminId
  );

const sortNotifications = (
  notifications: NotificationsRes[]
): NotificationsRes[] =>
  [...notifications].sort(
    (left, right) =>
      new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
  );

export const useNotificationFeed = () => {
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;
  const adminId = userData?.account?.admin?.id;
  const queryClient = useQueryClient();
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const locallyReadIds = useNotificationReadStore(
    (state) => state.locallyReadIds
  );
  const markLocallyRead = useNotificationReadStore(
    (state) => state.markLocallyRead
  );
  const locallyReadIdSet = useMemo(
    () => toLocallyReadIdSet(locallyReadIds),
    [locallyReadIds]
  );

  const {
    data: notificationsResponse,
    isLoading: isNotificationsLoading,
    isRefetching: isNotificationsRefetching,
    isError: isNotificationsError,
    refetch: refetchNotifications,
  } = useGet<NotificationsRes[]>(
    [endPoints.notifications, 'feed', adminType],
    endPoints.notifications,
    {
      params: {
        page: 1,
        page_size: FEED_PAGE_SIZE,
      },
    },
    {
      refetchInterval: POLL_INTERVAL_MS,
      refetchOnWindowFocus: true,
    }
  );

  const rawApiNotifications = useMemo(
    () => parseNotificationsList(notificationsResponse),
    [notificationsResponse]
  );

  const normalizedApiNotifications = useMemo(
    () => normalizeApiNotifications(rawApiNotifications),
    [rawApiNotifications]
  );

  const notifications = useMemo(() => {
    const sorted = sortNotifications(normalizedApiNotifications);
    const filtered = applyAudienceFilters(sorted, adminType, adminId);

    return applyLocalReadState(filtered, locallyReadIdSet);
  }, [normalizedApiNotifications, adminType, adminId, locallyReadIdSet]);

  // The feed only ever fetches a single page of FEED_PAGE_SIZE notifications
  // and paginates over that page client-side (see NotificationsList), so the
  // total the pager is told about must never exceed what was actually
  // fetched/filtered — otherwise it advertises pages beyond the loaded data
  // and those pages render empty. `notifications.length` is a hard cap; the
  // API's own count (which reflects the full, unfiltered/unpaginated set)
  // can only ever narrow that further, never extend it.
  const totalCount = useMemo(() => {
    const apiTotal = parseNotificationsTotalCount(
      notificationsResponse,
      notifications.length
    );

    return Math.min(apiTotal, notifications.length);
  }, [notifications.length, notificationsResponse]);

  const refetch = useCallback(async () => {
    await refetchNotifications();
  }, [refetchNotifications]);

  const refetchUnreadCount = useCallback(async () => {
    await queryClient.invalidateQueries([
      endPoints.notificationsUnreadCount,
      adminType,
    ]);
  }, [adminType, queryClient]);

  const markAsRead = useCallback(
    async (notification: NotificationsRes) => {
      if (notification.is_read || locallyReadIdSet.has(notification.id)) {
        return;
      }

      if (notification.id < 0) {
        markLocallyRead(notification.id);
        return;
      }

      try {
        await axiosInstance.patch(
          endPoints.notificationMarkRead(String(notification.id))
        );
        await Promise.all([refetchNotifications(), refetchUnreadCount()]);
      } catch {
        markLocallyRead(notification.id);
      }
    },
    [
      locallyReadIdSet,
      markLocallyRead,
      refetchNotifications,
      refetchUnreadCount,
    ]
  );

  const markAllAsRead = useCallback(async () => {
    setIsMarkingAllRead(true);

    try {
      await axiosInstance.patch(endPoints.notificationMarkAllRead);
      notifications
        .filter((notification) => !notification.is_read)
        .forEach((notification) => {
          if (notification.id < 0) {
            markLocallyRead(notification.id);
          }
        });
      await Promise.all([refetch(), refetchUnreadCount()]);
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [markLocallyRead, notifications, refetch, refetchUnreadCount]);

  return {
    notifications,
    isLoading: isNotificationsLoading,
    isRefetching: isNotificationsRefetching,
    isError: isNotificationsError,
    refetch,
    markAsRead,
    markAllAsRead,
    isMarkingAllRead,
    totalCount,
    adminType,
  };
};

export const useNotificationUnreadCount = () => {
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;

  const {
    data: unreadCountResponse,
    refetch: refetchUnreadCount,
    isLoading: isUnreadCountLoading,
    isError: isUnreadCountError,
  } = useGet<{ data: number } | number>(
    [endPoints.notificationsUnreadCount, adminType],
    endPoints.notificationsUnreadCount,
    undefined,
    {
      refetchInterval: POLL_INTERVAL_MS,
      refetchOnWindowFocus: true,
    }
  );

  const unreadCount = parseUnreadCount(unreadCountResponse);

  return {
    unreadCount,
    isUnreadCountLoading,
    isUnreadCountError,
    refetchUnreadCount,
  };
};
